import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listTemplates, instantiateTemplate, cloneTemplate,
  type WorkflowTemplate,
} from '../../api/workflowApi.js';
import { sx } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

/**
 * WorkflowTemplatesPage — reusable workflow template library (UI Audit M04 C2).
 *
 * Lists system + tenant templates (T058) and supports:
 *  - Instantiate → loads a template's JSON as the seed for a new definition.
 *  - Clone → duplicates a template as a new tenant-owned template.
 */
export function WorkflowTemplatesPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<WorkflowTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [instId, setInstId] = useState<string | null>(null);
  const [instCode, setInstCode] = useState('');
  const [instName, setInstName] = useState('');
  const [cloneId, setCloneId] = useState<string | null>(null);
  const [cloneCode, setCloneCode] = useState('');
  const [cloneName, setCloneName] = useState('');

  const load = useCallback(() => {
    setRows(null);
    listTemplates(token).then(setRows).catch((e) => setError((e as Error).message));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  async function doInstantiate(id: string): Promise<void> {
    if (!instCode.trim() || !instName.trim()) { setError(t('wf.templates.instReq')); return; }
    setBusyId(id); setError(null); setNotice(null);
    try {
      const res = await instantiateTemplate(token, id, { workflowCode: instCode.trim(), workflowName: instName.trim() });
      setNotice(res.message ?? t('wf.templates.instantiated'));
      setInstId(null); setInstCode(''); setInstName('');
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusyId(null); }
  }

  async function doClone(id: string): Promise<void> {
    if (!cloneCode.trim() || !cloneName.trim()) { setError(t('wf.templates.cloneReq')); return; }
    setBusyId(id); setError(null); setNotice(null);
    try {
      await cloneTemplate(token, id, { newTemplateCode: cloneCode.trim(), newTemplateName: cloneName.trim() });
      setNotice(t('wf.templates.cloned'));
      setCloneId(null); setCloneCode(''); setCloneName('');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusyId(null); }
  }

  return (
    <main aria-label="wf-templates" style={sx.main}>
      <div style={sx.headerRow}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/admin/workflows" style={sx.link}>{t('wf.common.backDesigner')}</Link>
          <h1>{t('wf.templates.title')}</h1>
        </div>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {!rows ? <p>{t('common.loading')}</p> : rows.length === 0 ? (
        <p data-testid="templates-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.templates.empty')}</p>
      ) : (
        <DataTable<WorkflowTemplate>
          ariaLabel="templates-table"
          rows={rows}
          rowKey={(tpl) => tpl.templateId}
          rowTestId={(tpl) => `template-row-${tpl.templateId}`}
          columns={[
            { key: 'code', header: t('wf.common.code'), render: (tpl) => tpl.templateCode },
            { key: 'name', header: t('wf.common.name'), render: (tpl) => tpl.templateName },
            { key: 'entity', header: t('wf.common.entity'), render: (tpl) => tpl.entityType ?? '—' },
            { key: 'system', header: t('wf.templates.colSystem'), render: (tpl) => (tpl.isSystemTemplate ? t('common.yes') : t('common.no')) },
            { key: 'used', header: t('wf.templates.colUsed'), render: (tpl) => tpl.usageCount },
            { key: 'actions', header: t('wf.common.actions'), render: (tpl) => {
              const rowBusy = busyId === tpl.templateId;
              return (
                instId === tpl.templateId ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <input aria-label={`inst-code-${tpl.templateId}`} value={instCode} placeholder={t('wf.templates.instCodePh')}
                      onChange={(e) => setInstCode(e.target.value)} style={{ padding: 4, border: '1px solid var(--color-border-default)', borderRadius: 4 }} />
                    <input aria-label={`inst-name-${tpl.templateId}`} value={instName} placeholder={t('wf.templates.instNamePh')}
                      onChange={(e) => setInstName(e.target.value)} style={{ padding: 4, border: '1px solid var(--color-border-default)', borderRadius: 4 }} />
                    <Button type="button" variant="primary" disabled={rowBusy}
                      aria-label={`inst-confirm-${tpl.templateId}`} onClick={() => doInstantiate(tpl.templateId)}>{t('wf.templates.go')}</Button>
                    <Button type="button" variant="secondary" onClick={() => setInstId(null)}>{t('common.cancel')}</Button>
                  </span>
                ) : cloneId === tpl.templateId ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <input aria-label={`clone-code-${tpl.templateId}`} value={cloneCode} placeholder={t('wf.templates.cloneCodePh')}
                      onChange={(e) => setCloneCode(e.target.value)} style={{ padding: 4, border: '1px solid var(--color-border-default)', borderRadius: 4 }} />
                    <input aria-label={`clone-name-${tpl.templateId}`} value={cloneName} placeholder={t('wf.templates.cloneNamePh')}
                      onChange={(e) => setCloneName(e.target.value)} style={{ padding: 4, border: '1px solid var(--color-border-default)', borderRadius: 4 }} />
                    <Button type="button" variant="primary" disabled={rowBusy}
                      aria-label={`clone-confirm-${tpl.templateId}`} onClick={() => doClone(tpl.templateId)}>{t('common.save')}</Button>
                    <Button type="button" variant="secondary" onClick={() => setCloneId(null)}>{t('common.cancel')}</Button>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                    <Button type="button" variant="primary"
                      aria-label={`instantiate-${tpl.templateId}`}
                      onClick={() => { setInstId(tpl.templateId); setCloneId(null); setInstCode(''); setInstName(''); }}>{t('wf.templates.use')}</Button>
                    <Button type="button" variant="secondary"
                      aria-label={`clone-template-${tpl.templateId}`}
                      onClick={() => { setCloneId(tpl.templateId); setInstId(null); setCloneCode(''); setCloneName(''); }}>{t('wf.templates.clone')}</Button>
                  </span>
                )
              );
            } },
          ]}
        />
      )}
    </main>
  );
}
